import React, { createContext, useContext, useState, useCallback } from "react";

const STORAGE_KEY = "am_selected_customer_id";

interface AccountManagerContextType {
  selectedCustomerId: number | null;
  setSelectedCustomerId: (id: number | null) => void;
  hasCustomerSelected: boolean;
}

const AccountManagerContext = createContext<
  AccountManagerContextType | undefined
>(undefined);

export const useAccountManager = (): AccountManagerContextType => {
  const ctx = useContext(AccountManagerContext);
  if (!ctx) {
    throw new Error(
      "useAccountManager must be used within AccountManagerProvider",
    );
  }
  return ctx;
};

interface AccountManagerProviderProps {
  children: React.ReactNode;
}

export const AccountManagerProvider: React.FC<AccountManagerProviderProps> = ({
  children,
}) => {
  const [selectedCustomerId, setSelectedCustomerIdState] = useState<
    number | null
  >(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored === null) return null;
    const parsed = Number(stored);
    return Number.isFinite(parsed) ? parsed : null;
  });

  const setSelectedCustomerId = useCallback((id: number | null) => {
    setSelectedCustomerIdState(id);
    if (id === null) {
      sessionStorage.removeItem(STORAGE_KEY);
    } else {
      sessionStorage.setItem(STORAGE_KEY, String(id));
    }
  }, []);

  return (
    <AccountManagerContext.Provider
      value={{
        selectedCustomerId,
        setSelectedCustomerId,
        hasCustomerSelected: selectedCustomerId !== null,
      }}
    >
      {children}
    </AccountManagerContext.Provider>
  );
};

export default AccountManagerProvider;
